/**
 * Outlier filtering for time-series telemetry charts.
 *
 * Modes:
 *   'off'        — no filtering.
 *   'light'      — physical-bounds clamp + rate-of-change gate against the last
 *                  good value, so short bursts of bad reads drop as a unit.
 *                  Predictable, no statistics.
 *   'aggressive' — physical clamp + rolling Hampel filter (window median +/-
 *                  k*MAD, with a relative floor so flat baselines still flag
 *                  spikes). Catches in-range spikes on any signal.
 *
 * Every mode DROPS a flagged point to null (never substitutes a value). The
 * chart renders with spanGaps so the line bridges the gap.
 */

// Hard physical bounds per BASE parameter key. Values outside these are
// impossible sensor reads (e.g. raw 0xFFFF registers) in any context.
export const OUTLIER_LIMITS = {
  totalVoltage: { min: 10, max: 1000 },
  maxCellVoltage: { min: 1000, max: 5000 },
  minCellVoltage: { min: 1000, max: 5000 },
  soc: { min: 0, max: 100 },
  soh: { min: 0, max: 100 },
  maxTemp: { min: -30, max: 100 },
  minTemp: { min: -30, max: 100 },
  current: { min: -1000, max: 1000 },
};

// Max plausible change between two consecutive samples, per BASE parameter.
// Only slow-moving signals are gated; fast signals (e.g. current) are omitted
// so legitimate swings are not clipped.
export const DELTA_LIMITS = {
  totalVoltage: 100,
  maxCellVoltage: 500,
  minCellVoltage: 500,
  soc: 30,
  soh: 10,
  maxTemp: 40,
  minTemp: 40,
};

// Hampel tuning
const HAMPEL_HALF_WINDOW = 7; // full window = 2*half + 1 = 15 samples; tolerates
                              // bursts of up to 7 bad reads before the median moves
const HAMPEL_K = 3;           // scaled-MAD deviations required to flag a point
const MAD_SCALE = 1.4826;     // scales MAD to a std-dev equivalent for normal data
const HAMPEL_FLOOR_FRAC = 0.02; // minimum dispersion as a fraction of |median|, so a
                                // flat baseline (MAD=0) still flags large spikes

// Consecutive samples the light gate may reject before it assumes a genuine
// level shift (not a burst of bad reads) and re-baselines. Sized above the
// typical run of clustered bad reads so whole bursts drop, while a real step
// loses at most this many points instead of the rest of the series.
const LIGHT_MAX_REJECT_RUN = 8;

/**
 * Reduce a possibly-composite widget key ("dev123_5_totalVoltage") to its base
 * parameter key ("totalVoltage"). Param keys are camelCase with no underscores,
 * so the segment after the final underscore is always the param.
 */
export function baseParamKey(key) {
  const s = String(key);
  const i = s.lastIndexOf('_');
  return i === -1 ? s : s.slice(i + 1);
}

/** True if val is outside the hard physical bounds for key's base param. */
export function isPhysicalOutlier(key, val) {
  const limit = OUTLIER_LIMITS[baseParamKey(key)];
  if (!limit) return false;
  return val < limit.min || val > limit.max;
}

function median(nums) {
  if (nums.length === 0) return NaN;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// LIGHT: physical clamp + rate-of-change gate against the last GOOD value.
// Bad reads arrive in short bursts of identical values (delta == 0 inside the
// burst), so comparing against the last good value (not the previous sample)
// drops the whole burst. A reject run longer than LIGHT_MAX_REJECT_RUN is taken
// as a genuine level shift and re-baselined, bounding the damage a real step
// could otherwise do.
function lightFilter(values, key) {
  const delta = DELTA_LIMITS[baseParamKey(key)];
  let lastGood = null;
  let rejectRun = 0;
  return values.map((v) => {
    if (v === null) return null;
    if (isPhysicalOutlier(key, v)) return null;
    if (delta !== undefined && lastGood !== null && Math.abs(v - lastGood) > delta) {
      rejectRun += 1;
      if (rejectRun > LIGHT_MAX_REJECT_RUN) {
        lastGood = v;
        rejectRun = 0;
        return v;
      }
      return null;
    }
    lastGood = v;
    rejectRun = 0;
    return v;
  });
}

// AGGRESSIVE: physical clamp + rolling Hampel over the RAW series. Each point is
// judged against the median + scaled-MAD of its window (median resists the
// outlier itself). BMS baselines are very flat, so MAD is frequently 0; a
// relative floor (a fraction of |median|) stops the threshold collapsing to
// zero, which is what let spikes through on flat data.
function aggressiveFilter(values, key) {
  return values.map((v, i) => {
    if (v === null) return null;
    if (isPhysicalOutlier(key, v)) return null;
    const window = [];
    for (let j = i - HAMPEL_HALF_WINDOW; j <= i + HAMPEL_HALF_WINDOW; j++) {
      if (j >= 0 && j < values.length && values[j] !== null) window.push(values[j]);
    }
    if (window.length < 2) return v; // not enough context to judge
    const med = median(window);
    const mad = median(window.map((x) => Math.abs(x - med)));
    const threshold = Math.max(HAMPEL_K * MAD_SCALE * mad, HAMPEL_FLOOR_FRAC * Math.abs(med));
    if (threshold === 0) return v; // constant zero window: nothing to flag
    return Math.abs(v - med) > threshold ? null : v;
  });
}

/**
 * Apply the chosen outlier filter to a numeric series (nulls allowed for
 * already-missing points). Returns a NEW array; the input is not mutated.
 *
 * @param {(number|null)[]} values
 * @param {string} key   series key (a composite widget key is fine)
 * @param {'off'|'light'|'aggressive'} mode
 * @returns {(number|null)[]}
 */
export function applyOutlierFilter(values, key, mode) {
  switch (mode) {
    case 'light':
      return lightFilter(values, key);
    case 'aggressive':
      return aggressiveFilter(values, key);
    case 'off':
    default:
      return [...values];
  }
}
